import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import { expr } from '@codegrade/utils';
import * as buttons from '@/components/Button';
import { Icon } from '@/components/Icon';
import * as utils from '@/utils';
import './style.css';
const ActionIcon = tsx.component({
    name: 'action-icon',
    functional: true,
    props: {
        icon: p.ofType().required,
    },
    /** @returns The component VNode. */
    render(h, ctx) {
        return (<Icon data-cy="action-icon" icon={ctx.props.icon} class={['size-5', ctx.data.class, ctx.data.staticClass]}/>);
    },
});
const TileHeader = tsx.component({
    name: 'tile-header',
    props: {
        entityIcon: p.ofType().optional,
        title: p.ofType().required,
    },
    /** @returns The component VNode. */
    render() {
        return (<div data-cy="tile-header" class="flex flex-row items-baseline gap-2 p-3 pb-2">
                {utils.ifJustOrEmpty(Maybe.fromNullable(this.entityIcon), icon => (
            // These icons never align well with text, so we need
            // a tiny adjustment of a couple of pixels.
            <ActionIcon icon={icon} class="grow-0 shrink-0 translate-y-[2px]"/>))}

                <h2 class="grow title-medium wrap-anywhere hyphens-auto m-0">{this.title}</h2>

                {utils.ifJustOrEmpty(Maybe.fromNullable(this.$slots.default), content => (<div data-cy="tile-header-extra" class="grow-0 shrink-0">
                        {content}
                    </div>))}
            </div>);
    },
});
const commonProps = {
    entityIcon: p.ofType().optional,
    title: p(String).required,
    description: p.ofType().required,
    semantic: p.ofType().default('actionable'),
    prominence: p.ofType().default(2),
};
/**
 * This is a tile that takes a single action which it performs when clicked or
 * otherwise activated.
 */
export const Single = tsx.componentFactoryOf().create({
    name: 'single-action-tile',
    props: {
        ...commonProps,
        action: p.ofType().required,
    },
    computed: {
        /** @returns The tile header. */
        headerNode() {
            return (<TileHeader entityIcon={this.entityIcon} title={this.title}>
                    {utils.renderScopedSlot(undefined, this.$scopedSlots.headerExtra, utils.emptyVNode)}
                </TileHeader>);
        },
        /** @returns The classes to apply to all single action tiles. */
        actionClasses() {
            return ['action-tile single', 'flex flex-col justify-between'];
        },
        /** @returns The classes to apply to button single action tiles. */
        buttonActionClasses() {
            return [this.actionClasses, 'h-full p-0', 'items-stretch', 'text-left text-base'];
        },
    },
    methods: {
        /**
         * Render a link action.
         *
         * @param action The action to render a link for.
         * @returns The link for a `link-route` action.
         */
        renderRouterLink(action) {
            const disabled = action.disabled === true;
            return (<router-link data-cy="single-action-tile" class={this.actionClasses} to={disabled ? this.$route.fullPath : action.route} data-semantic={this.semantic} data-prominence={this.prominence} aria-disabled={disabled ? 'true' : undefined} aria-describedby={action.ariaDescribedBy} aria-label={action.ariaLabel}>
                    {this.renderContent()}
                </router-link>);
        },
        /**
         * Render a sync action button.
         *
         * @param action The action to render a button for.
         * @returns The button for a `call-sync` action.
         */
        renderSyncButton(action) {
            return (<buttons.Button data-cy="single-action-tile" buttonClasses={this.buttonActionClasses} action={action.action} disabled={action.disabled} semantic={this.semantic} prominence={this.prominence} ariaDescribedBy={action.ariaDescribedBy} ariaLabel={action.ariaLabel}>
                    {this.renderContent()}
                </buttons.Button>);
        },
        /**
         * Render an async action button.
         *
         * @param action The action to render a button for.
         * @returns The button for a `call-async` action.
         */
        renderAsyncButton(action) {
            return (<buttons.PromiseButton data-cy="single-action-tile" buttonClasses={this.buttonActionClasses} semantic={this.semantic} prominence={this.prominence} action={action.action} disabled={action.disabled} ariaLabel={action.ariaLabel} ariaDescribedBy={action.ariaDescribedBy} loadingAlertMessage={action.loadingAlertMessage} successAlertMessage={action.successAlertMessage} successDuration={action.successDuration} scopedSlots={{
                    content: ({ statusIcon }) => this.renderContent(statusIcon),
                }}/>);
        },
        /** @returns The tile body. */
        renderBody(icon) {
            return (<div data-cy="tile-body">
                    <div class="label-medium flex flex-row items-center gap-2 px-3 py-2">
                        <div class="grow shrink">{this.description}</div>

                        {icon ?? <ActionIcon icon={this.action.icon}/>}
                    </div>
                </div>);
        },
        /** @returns The tile content. */
        renderContent(icon) {
            return [this.headerNode, this.renderBody(icon)];
        },
    },
    /** @returns The component VNode. */
    render() {
        return expr.match(this.action, 'tag', {
            'link-route': action => this.renderRouterLink(action),
            'call-sync': action => this.renderSyncButton(action),
            'call-async': action => this.renderAsyncButton(action),
        });
    },
});
/**
 * This is a tile that has a list of actions to take when clicked or otherwise
 * activated, and an optional link for its title.
 */
export const Multi = tsx.componentFactoryOf().create({
    name: 'multi-action-tile',
    props: {
        ...commonProps,
        titleLink: p.ofType().optional,
        actions: p.ofType().required,
    },
    computed: {
        /** @returns The tile header. */
        headerNode() {
            const title = Maybe.fromNullable(this.titleLink).caseOf({
                Nothing: () => this.title,
                Just: route => (<router-link to={route} class="text-inherit">
                        {this.title}
                    </router-link>),
            });
            return (<TileHeader entityIcon={this.entityIcon} title={title}>
                    {utils.renderScopedSlot(undefined, this.$scopedSlots.headerExtra, utils.emptyVNode)}
                </TileHeader>);
        },
        /** @returns The tile body. */
        bodyNode() {
            return (<div data-cy="tile-body">
                    <div class="label-medium px-3 py-2">{this.description}</div>

                    {this.actionsListNode}
                </div>);
        },
        /** @returns The list of actions. */
        actionsListNode() {
            const lastIndex = this.actions.length - 1;
            return (<div data-cy="tile-actions" class="flex flex-col items-stretch">
                    {this.actions.map((action, i) => ({
                    ...this.renderAction(action, i === lastIndex),
                    key: i,
                }))}
                </div>);
        },
    },
    methods: {
        /** @returns The classes to apply to link actions. */
        getActionClasses(isLast) {
            return [
                'action',
                'flex flex-row items-center',
                'py-2 px-3 gap-2',
                'border-t',
                this.getRoundedClasses(isLast),
            ];
        },
        /** @returns The classes to apply to button actions. */
        getButtonActionClasses(isLast) {
            return [
                this.getActionClasses(isLast),
                'border-b-0 border-x-0',
                'not-last:rounded-none last:rounded-t-none',
                'text-left text-base',
            ];
        },
        /**
         * Get the correct `rounded-b-X` class based on action index.
         *
         * Note: we CANNOT use tailwind's `last:` and `not-last:` specifiers,
         * because the `PromiseButton` gets wrapped in a `<div>` and so the
         * button itself will ALWAYS be the last child.
         *
         * Without rounding the bottom of the last item it would overflow the
         * parent, overlapping part of the parent's rounded border.
         *
         * We cannot set the `overflow-hidden` class on the parent to fix this,
         * though, because that breaks popovers that the buttons in an action tile
         * might show (such as a confirmation popover).
         *
         * The radius here is slightly smaller than the radius on the parent
         * component, so that this element's outer border radius lines up with the
         * parent's inner radius. If we'd use the same size as the parent, there
         * would be a small gap between this element's background and the parent's
         * border.
         *
         * @param isLast Whether this is the last action in the list.
         * @returns The rounded classes for this action.
         */
        getRoundedClasses(isLast) {
            return isLast ? 'rounded-b-md!' : 'rounded-b-none!';
        },
        /**
         * Render an action in the list of actions.
         *
         * @param action The action to render a list item for.
         * @returns An action list item.
         */
        renderAction(action, isLast) {
            return expr.match(action, 'tag', {
                'link-route': a => this.renderRouterLink(a, isLast),
                'call-sync': a => this.renderSyncButton(a, isLast),
                'call-async': a => this.renderAsyncButton(a, isLast),
            });
        },
        /**
         * Render a link action.
         *
         * @param action The action to render a link for.
         * @returns The link for a `link-route` action.
         */
        renderRouterLink(action, isLast) {
            const disabled = action.disabled === true;
            return (<router-link to={disabled ? this.$route.fullPath : action.route} class={this.getActionClasses(isLast)} aria-disabled={disabled ? 'true' : undefined} aria-describedby={action.ariaDescribedBy} aria-label={action.ariaLabel}>
                    <span class="grow shrink">{action.label}</span>
                    <ActionIcon icon={action.icon} class="grow-0 shrink-0"/>
                </router-link>);
        },
        /**
         * Render a sync action button.
         *
         * @param action The action to render a button for.
         * @returns The button for a `call-sync` action.
         */
        renderSyncButton(action, isLast) {
            return (<buttons.Button buttonClasses={[this.getButtonActionClasses(isLast), 'items-stretch']} action={action.action} disabled={action.disabled} semantic={this.semantic} prominence={this.prominence} ariaDescribedBy={action.ariaDescribedBy} ariaLabel={action.ariaLabel}>
                    <span class="grow shrink">{action.label}</span>
                    <ActionIcon icon={action.icon} class="grow-0 shrink-0"/>
                </buttons.Button>);
        },
        /**
         * Render an async action button.
         *
         * @param action The action to render a button for.
         * @returns The button for a `call-async` action.
         */
        renderAsyncButton(action, isLast) {
            return (<buttons.PromiseButton buttonClasses={this.getButtonActionClasses(isLast)} semantic={this.semantic} prominence={this.prominence} action={action.action} disabled={action.disabled} ariaLabel={action.ariaLabel} ariaDescribedBy={action.ariaDescribedBy} loadingAlertMessage={action.loadingAlertMessage} successAlertMessage={action.successAlertMessage} successDuration={action.successDuration} waitAtLeast={action.waitAtLeast} scopedSlots={{
                    content: ({ statusIcon }) => [
                        <span class="grow shrink">{action.label}</span>,
                        statusIcon ?? <ActionIcon icon={action.icon} class="grow-0 shrink-0"/>,
                    ],
                }}/>);
        },
    },
    /** @returns The component VNode. */
    render() {
        return (<div data-cy="multi-action-tile" data-semantic={this.semantic} data-prominence={this.prominence} class="action-tile multi flex flex-col justify-between">
                {this.headerNode}
                {this.bodyNode}
            </div>);
    },
});
